import { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Send,
  Phone,
  MoreVertical,
  Clock,
  CheckCheck,
  User,
  Bot,
  RefreshCw,
  AlertCircle,
  Search,
  X,
  Pause,
  Play,
} from "lucide-react";
import { api } from "../../services/api";

export default function ChatsPage() {
  const { id, cid } = useParams();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isTogglingAI, setIsTogglingAI] = useState(false);
  const messagesEndRef = useRef(null);
  const searchInputRef = useRef(null);
  const menuRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const convResponse = await api.getConversation(id, cid);

      setConversation(convResponse.conversation);
      // Messages are returned as a separate field from the API, not nested in conversation
      setConversationMessages(convResponse.messages || []);
    } catch (err) {
      console.error("Failed to fetch conversation:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [id, cid]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    scrollToBottom();
  }, [conversationMessages]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when search mode is activated
  useEffect(() => {
    if (isSearching && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearching]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Format phone number with spacing for readability (e.g., 254 723 068 001)
  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    // Remove any existing spaces or special characters
    const cleaned = phone.replace(/\D/g, "");
    // Format as groups of 3-4 digits
    if (cleaned.length <= 4) return cleaned;
    if (cleaned.length <= 7) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
    if (cleaned.length <= 10) return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
    // For longer numbers (like international), group as: country code + 3 + 3 + rest
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9)}`;
  };

  const handleSearchToggle = () => {
    setIsSearching(!isSearching);
    setSearchQuery("");
    setShowMenu(false);
  };

  // Toggle AI pause status for this conversation
  const handleToggleAIPause = async () => {
    if (isTogglingAI) return;

    const newPausedState = !conversation.aiPaused;
    setIsTogglingAI(true);

    try {
      await api.updateConversationAIPaused(id, cid, newPausedState);
      setConversation((prev) => ({ ...prev, aiPaused: newPausedState }));
    } catch (err) {
      console.error("Failed to toggle AI pause:", err);
      setError(err.message);
    } finally {
      setIsTogglingAI(false);
      setShowMenu(false);
    }
  };

  // Format AI response text: convert **text** to bold, remove asterisks, and preserve line breaks
  const formatAIText = (text) => {
    if (!text) return null;

    // Split by line breaks first to preserve them
    const lines = text.split(/(\n)/g);

    return lines.map((line, lineIndex) => {
      // Handle line breaks
      if (line === "\n") {
        return <br key={`br-${lineIndex}`} />;
      }

      // Process **bold** patterns, then remove any remaining asterisks
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      return (
        <span key={`line-${lineIndex}`}>
          {parts.map((part, partIndex) => {
            if (part.startsWith("**") && part.endsWith("**")) {
              // Convert **text** to bold (removing the asterisks)
              return (
                <strong key={`${lineIndex}-${partIndex}`} className="font-semibold">
                  {part.slice(2, -2)}
                </strong>
              );
            }
            // Remove any remaining single asterisks from regular text
            return part.replace(/\*/g, "");
          })}
        </span>
      );
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-700">{error}</span>
            </div>
          )}
          <h2 className="text-xl font-bold text-text-primary mb-2">
            Conversation not found
          </h2>
          <button
            onClick={() => navigate(`/agent/${id}/conversations`)}
            className="text-primary hover:underline"
          >
            Back to Conversations
          </button>
        </div>
      </div>
    );
  }

  const handleSend = async () => {
    if (!newMessage.trim() || isSending) return;

    const messageText = newMessage.trim();
    setNewMessage("");
    setIsSending(true);
    setError(null);

    // Optimistically add the message to the UI
    const tempMessage = {
      id: `temp-${Date.now()}`,
      role: "assistant",
      content: messageText,
      timestamp: new Date().toISOString(),
    };
    setConversationMessages((prev) => [...prev, tempMessage]);

    try {
      const response = await api.sendMessageToConversation(id, cid, messageText);
      // Update the temp message with the real ID from the server
      if (response.message_id) {
        setConversationMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempMessage.id ? { ...msg, id: response.message_id } : msg
          )
        );
      }
    } catch (err) {
      console.error("Failed to send message:", err);
      setError(err.message);
      // Remove the optimistic message on error
      setConversationMessages((prev) =>
        prev.filter((msg) => msg.id !== tempMessage.id)
      );
    } finally {
      setIsSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString([], {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
  };

  // Transform messages to expected format
  const messages = conversationMessages.map((msg) => ({
    id: msg.id,
    type: msg.role === "user" ? "user" : "ai",
    text: msg.content,
    time: msg.timestamp,
  }));

  // Get contact info - prioritize contact name for display, fall back to phone
  // API returns 'phone' and 'contactName' fields
  const contactNumber = conversation.phone || "";
  const displayName = conversation.contactName && conversation.contactName !== contactNumber
    ? conversation.contactName
    : (contactNumber ? formatPhoneNumber(contactNumber) : "Unknown");

  // Filter messages based on search query
  const filteredMessages = searchQuery
    ? messages.filter((msg) =>
        msg.text?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : messages;

  // Group filtered messages by date
  const groupedMessages = filteredMessages.reduce((groups, message) => {
    const date = formatDate(message.time);
    if (!groups[date]) groups[date] = [];
    groups[date].push(message);
    return groups;
  }, {});

  return (
    <div className="h-screen flex flex-col bg-background animate-fade-in">
      {/* Header */}
      <header className="bg-white border-b border-border px-6 py-4 flex items-center gap-4">
        <button
          onClick={() => navigate(`/agent/${id}/conversations`)}
          className="p-2 hover:bg-background rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-text-secondary" />
        </button>

        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
            <Phone className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-semibold text-text-primary flex items-center gap-2">
              {displayName}
            </h1>
            {conversation.contactName && conversation.contactName !== contactNumber && contactNumber && (
              <p className="text-xs text-text-muted">
                {formatPhoneNumber(contactNumber)}
              </p>
            )}
            <p className="text-sm text-text-muted">
              {messages.length} messages
            </p>
          </div>
        </div>

        {/* Refresh Button */}
        <button
          onClick={fetchData}
          disabled={isLoading}
          className="p-2 hover:bg-background rounded-lg transition-colors disabled:opacity-50"
          title="Refresh messages"
        >
          <RefreshCw className={`w-5 h-5 text-text-secondary ${isLoading ? 'animate-spin' : ''}`} />
        </button>

        {/* Menu Button */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-background rounded-lg transition-colors"
          >
            <MoreVertical className="w-5 h-5 text-text-secondary" />
          </button>

          {/* Dropdown Menu */}
          {showMenu && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl border border-border shadow-elevated z-50 py-1 animate-fade-in">
              <button
                onClick={handleSearchToggle}
                className="w-full px-4 py-2.5 text-left text-sm text-text-primary hover:bg-gray-50 flex items-center gap-3"
              >
                <Search className="w-4 h-4 text-text-muted" />
                Search messages
              </button>
              <div className="border-t border-border my-1" />
              <button
                onClick={handleToggleAIPause}
                disabled={isTogglingAI}
                className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-3 disabled:opacity-50
                  ${conversation.aiPaused ? 'text-green-600' : 'text-amber-600'}`}
              >
                {conversation.aiPaused ? (
                  <>
                    <Play className="w-4 h-4" />
                    Resume AI responses
                  </>
                ) : (
                  <>
                    <Pause className="w-4 h-4" />
                    Pause AI responses
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-3 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-red-700 text-sm flex-1">{error}</span>
          <button
            onClick={() => setError(null)}
            className="p-1 hover:bg-red-100 rounded transition-colors"
          >
            <X className="w-4 h-4 text-red-500" />
          </button>
        </div>
      )}

      {/* AI Paused Banner */}
      {conversation.aiPaused && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-center gap-3">
          <Pause className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <span className="text-amber-800 text-sm flex-1">
            AI responses are paused for this conversation. You are now handling this chat manually.
          </span>
          <button
            onClick={handleToggleAIPause}
            disabled={isTogglingAI}
            className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-700 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <Play className="w-4 h-4" />
            Resume AI
          </button>
        </div>
      )}

      {/* Search Bar */}
      {isSearching && (
        <div className="bg-white border-b border-border px-6 py-3 animate-slide-up">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search in conversation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2 bg-gray-50 rounded-lg border border-border text-sm
                         focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
            <button
              onClick={handleSearchToggle}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded"
            >
              <X className="w-4 h-4 text-text-muted" />
            </button>
          </div>
          {searchQuery && (
            <p className="text-xs text-text-muted text-center mt-2">
              Found {filteredMessages.length} matching message{filteredMessages.length !== 1 ? "s" : ""}
            </p>
          )}
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto py-4 space-y-6 bg-gradient-to-b from-background to-white">
        <div className="max-w-2xl mx-auto px-4">
          {Object.keys(groupedMessages).length === 0 ? (
            <div className="flex items-center justify-center h-full min-h-[300px]">
              <p className="text-text-muted">
                {searchQuery ? `No messages matching "${searchQuery}"` : "No messages in this conversation"}
              </p>
            </div>
          ) : (
            Object.entries(groupedMessages).map(([date, msgs]) => (
              <div key={date} className="mb-6">
                {/* Date Divider */}
                <div className="flex items-center justify-center mb-4">
                  <span className="bg-white px-4 py-1 rounded-full text-xs text-text-muted shadow-sm border border-border">
                    {date}
                  </span>
                </div>

                {/* Messages */}
                <div className="space-y-3">
                  {msgs.map((message, index) => (
                    <div
                      key={message.id || index}
                      className={`flex ${
                        message.type === "user" ? "justify-start" : "justify-end"
                      } animate-slide-up`}
                      style={{ animationDelay: `${index * 30}ms` }}
                    >
                      <div
                        className={`max-w-[85%] sm:max-w-[70%] ${
                          message.type === "user" ? "order-2" : "order-1"
                        }`}
                      >
                        <div
                          className={`
                          px-4 py-3 rounded-2xl
                          ${
                            message.type === "user"
                              ? "bg-white border border-border rounded-tl-sm"
                              : "bg-gradient-to-br from-primary to-accent text-white rounded-tr-sm"
                          }
                        `}
                        >
                          <p className="text-sm leading-relaxed">
                            {message.type === "ai" ? formatAIText(message.text) : message.text}
                          </p>
                        </div>
                        <div
                          className={`flex items-center gap-1 mt-1 px-1 ${
                            message.type === "user" ? "" : "justify-end"
                          }`}
                        >
                          <Clock className="w-3 h-3 text-text-muted" />
                          <span className="text-xs text-text-muted">
                            {formatTime(message.time)}
                          </span>
                          {message.type === "ai" && (
                            <CheckCheck className="w-3 h-3 text-primary ml-1" />
                          )}
                        </div>
                      </div>

                      {/* Avatar */}
                      <div
                        className={`flex-shrink-0 ${
                          message.type === "user" ? "order-1 mr-2" : "order-2 ml-2"
                        }`}
                      >
                        <div
                          className={`
                          w-8 h-8 rounded-full flex items-center justify-center
                          ${
                            message.type === "user"
                              ? "bg-gray-100"
                              : "bg-gradient-to-br from-primary/20 to-accent/20"
                          }
                        `}
                        >
                          {message.type === "user" ? (
                            <User className="w-4 h-4 text-text-secondary" />
                          ) : (
                            <Bot className="w-4 h-4 text-primary" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-border p-4">
        <div className="flex items-center gap-3 max-w-2xl mx-auto">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Type a message to send via WhatsApp..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
              disabled={isSending}
              className="input-field pr-12 disabled:opacity-50"
            />
          </div>
          <button
            onClick={handleSend}
            disabled={!newMessage.trim() || isSending}
            className="btn-primary p-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSending ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        <p className="text-center text-xs text-text-muted mt-2">
          Messages sent here will be delivered to the customer via WhatsApp
        </p>
      </div>
    </div>
  );
}